'use client';

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { unitPriceFor } from '@xop/dashboard-shared';
import { AlertTriangle } from 'lucide-react';
import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SchedulingCard } from './generate-invoice-form/scheduling-card';
import { ProductsTable, type InvoiceProduct, type ProductSelectionMode } from './generate-invoice-form/products-table';
import { BillToShipToCard } from './generate-invoice-form/bill-to-ship-to-card';
import { TotalsCard } from './generate-invoice-form/totals-card';
import {
  CustomerOrResellerPicker,
  customerOption,
  resellerOption,
  type RecipientOption,
} from './generate-invoice-form/customer-or-reseller-picker';
import { InvoiceDetailsCard } from './generate-invoice-form/invoice-details-card';
import {
  loadInvoiceData as loadInvoiceDataUtil,
  loadQuoteDataForConversion as loadQuoteDataForConversionUtil,
  loadInvoiceDataForDuplication as loadInvoiceDataForDuplicationUtil,
  loadInvoiceDataFromDraft as loadInvoiceDataFromDraftUtil,
  type OttoInvoiceDraft,
} from './generate-invoice-form/data-loaders';
import {
  inferDocumentType,
  highestSequence,
  formatDocumentNumber,
  fallbackDocumentNumber,
  sequenceSeed,
} from './generate-invoice-form/documentNumber';
import {
  validateInvoiceForm,
  type InvoiceValidationErrors,
} from './generate-invoice-form/validation';
import { buildInvoicePayload } from './generate-invoice-form/buildInvoicePayload';
import resellersApiService from '@crm-src/lib/resellers-api';
import { addressFields } from './generate-invoice-form/addressFields';
import {
  applyCatalogFeature,
  createProductLine,
  resolvePercentLines,
  removeProduct,
  updateProductField,
  withProductMode,
} from './generate-invoice-form/productLines';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@crm-src/hooks/useAuth';
import usePermissions from '@crm-src/hooks/usePermissions';
import { useResellerAccess } from '@crm-src/components/reseller';
import api from '@crm-src/lib/api';
import { dealsApi, type Deal } from '@crm-src/lib/deals-api';
import { productsApi } from '@crm-src/lib/products-api';
import { invoicesApi } from '@crm-src/lib/invoices-api';
import { applyInvoiceFieldChange, createInitialFormData, defaultTerms } from './generate-invoice-form/formData';
import { firstArrayField, personDisplayName } from './generate-invoice-form/apiNormalize';
import { computeInvoiceTotals } from '@crm-src/lib/invoice-totals';
import { splitInvoiceServerErrors } from '@crm-src/lib/invoice-errors';

/**
 * Create handlers report back so the form can surface server validation
 * errors in its own persistent banner + on the offending field, instead
 * of the parent silently toasting them (the toast auto-dismissed and the
 * errors "never appeared on screen"). Returning `void` stays supported
 * for any caller that hasn't been migrated.
 */
export interface InvoiceSubmitResult {
  success: boolean;
  errors?: string[];
}

interface GenerateInvoiceFormProps {
  onBack: () => void;
  onCancel: () => void;
  onSubmit: (invoiceData: any) => void | Promise<InvoiceSubmitResult | void>;
  isCreating: boolean;
  preSelectedCustomerId?: string | null;
  preSelectedDocumentType?: 'invoice' | 'quote';
  invoiceId?: string | null; // For edit mode
  onInvoiceUpdated?: () => void; // Callback after successful update
  isConvertingFromQuote?: boolean; // Lock document type when converting quote to invoice
  quoteToConvert?: string | null; // Quote ID to load data from for conversion
  duplicateFrom?: string | null; // Invoice ID to load data from for duplication
  // A fully-formed draft handed over by the agentic Staff Otto ("Otto Agent").
  // Unlike the id-based triggers above, there is no source doc to fetch — the
  // form hydrates straight from this in-memory object. See docs/otto-agent.md.
  ottoDraft?: OttoInvoiceDraft | null;
  /** Raise a document addressed to a RESELLER ITSELF (2026-09-13) — XOP's own
   * business with them, which has no customer and no agreement behind it. The
   * picker opens with the reseller selected, the bill-to is theirs, and the
   * number is `<their code>-0000-NNN`. crm-api has always accepted such a row;
   * this is the command center raising one deliberately. Since 2026-09-15 the
   * same state is reached by picking a reseller in the generator's own
   * "Customer or reseller" picker — this prop is the deep link, not the only
   * way in. */
  billToResellerId?: string | null;
}

export const GenerateInvoiceForm: React.FC<GenerateInvoiceFormProps> = ({
  onBack,
  onCancel,
  onSubmit,
  isCreating,
  preSelectedCustomerId,
  preSelectedDocumentType = 'invoice',
  invoiceId = null,
  onInvoiceUpdated,
  isConvertingFromQuote = false,
  quoteToConvert = null,
  duplicateFrom = null,
  ottoDraft = null,
  billToResellerId: billToResellerIdProp = null
}) => {
  const { user } = useAuth();
  // Salesperson wall: only an admin (super_admin in the CRM SPA) may name a
  // DIFFERENT salesperson on a quote/invoice. A non-admin is locked to
  // themselves (auto-set on new docs; the picker is disabled). crm-api enforces
  // the same rule server-side — see InvoicesController create/update.
  const { isAdmin } = usePermissions();
  const canEditSalesPerson = isAdmin();
  // Reseller wall: a viewer without crm:resellers must not have the invoice
  // bill-to silently auto-filled with the reseller's (or its dealers') details —
  // it falls back to billing the customer directly. See components/reseller.tsx.
  const resellerAccess = useResellerAccess();

  // Debug logging on component mount
  
  // Determine if we're in edit mode
  const isEditMode = !!invoiceId;
  
  // Customer/Contact state
  const [contacts, setContacts] = useState<any[]>([]);
  const [selectedContact, setSelectedContact] = useState<any | null>(null);
  // The RESELLER this document is addressed to, when it is addressed to one
  // rather than to a customer. `addressedResellerId` is the MODE (set by the
  // `?resellerId=` deep link, by picking a reseller in the picker, or by
  // reopening a reseller-addressed document); `billToReseller` is the loaded
  // record behind it. Both null in the ordinary customer flow. The ref mirrors
  // the id synchronously so an in-flight reseller load can tell it has been
  // superseded by a later pick (customer or another reseller).
  const [addressedResellerId, setAddressedResellerId] = useState<string | null>(
    billToResellerIdProp ? String(billToResellerIdProp) : null,
  );
  const addressedResellerRef = useRef<string | null>(addressedResellerId);
  const [billToReseller, setBillToReseller] = useState<any | null>(null);
  const billsAReseller = !!addressedResellerId;
  
  // Users/Sales people state
  const [users, setUsers] = useState<any[]>([]);
  const [selectedSalesPerson, setSelectedSalesPerson] = useState<any | null>(null);

  // Whether the contacts + salespeople list fetches have COMPLETED (success OR
  // failure). The edit/duplicate/convert loaders gate on these, NOT on
  // `length > 0`: an empty or failed list (e.g. the customers endpoint timing
  // out for a big tenant, or /users/salespeople returning nothing) must NOT
  // block loading the document — the loaders fall back to the doc's own snapshot
  // (`resolveFormCustomer`) and the salesperson match is optional. Gating on
  // `length > 0` was why Edit/Duplicate silently showed a blank form.
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  
  // Product selection state - hierarchical data for cascading dropdowns
  const [productHierarchy, setProductHierarchy] = useState<{
    products: any[];
    productFeatures: { [productId: number]: any[] };
  }>({ products: [], productFeatures: {} });
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  // The XOP price list (System Admin ▸ Price List) — the shared book a line
  // can be picked from instead of re-typed. Read through the DASHBOARD api
  // (not crmApiUrl): the catalogue is the dashboard's own, and the same
  // session that opened this form already authorises it.
  
  // Loading state for invoice number generation
  const [isGeneratingInvoiceNumber, setIsGeneratingInvoiceNumber] = useState(false);
  
  // Loading state for edit mode
  // Start in the loading state whenever we're opening an EXISTING document
  // (edit / duplicate / convert) so the reader sees a spinner immediately — NOT
  // the empty "Customer or reseller" picker, which is only for manually creating a new
  // doc. The document loaders clear this in their `finally`. (The trigger props
  // can arrive a render after mount, so an effect below also flips it on.)
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(
    !!(invoiceId || duplicateFrom || quoteToConvert),
  );
  
  // Removed multi-step approach - now showing form when both customer and deal are selected

  // Customer deals state - simplified to automatically use first deal
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [isLoadingDeals, setIsLoadingDeals] = useState(false);

  // Form validation state
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [validationErrors, setValidationErrors] = useState<InvoiceValidationErrors>({});
  
  // Track string representation of prices to preserve trailing zeros while typing
  const [priceInputs, setPriceInputs] = useState<{ [productId: string]: string }>({});
  
  // Bill To recipient selection state
  const [billToRecipientType, setBillToRecipientType] = useState<'reseller' | 'dealer' | null>(null);
  const [selectedDealerId, setSelectedDealerId] = useState<string | null>(null);

  // Scheduling state — recurring-CREATION only. Recurring schedules generate
  // invoices on a cadence; they never send to the customer (the generated
  // invoice is emailed to finance for review), so there is no auto-send /
  // one-off "Schedule Send" / recipient state here anymore.
  const [enableRecurring, setEnableRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState<'weekly' | 'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [recurringInterval, setRecurringInterval] = useState(1);
  const [recurringStartDate, setRecurringStartDate] = useState('');
  const [recurringDayOfWeek, setRecurringDayOfWeek] = useState(1); // Monday
  const [recurringDayOfMonth, setRecurringDayOfMonth] = useState(1);
  // Invoices bill in ARREARS: each occurrence is generated at the END of the
  // period it covers. 1 = the period's last day.
  const [recurringGenerateDaysBeforePeriodEnd, setRecurringGenerateDaysBeforePeriodEnd] = useState(1);
  const [existingScheduleId, setExistingScheduleId] = useState<string | null>(null); // Track existing schedule for updates
  
  // Form state
  const [formData, setFormData] = useState(() => createInitialFormData(preSelectedDocumentType));

  // The product list the form actually shows, validates, totals and sends:
  // the stored lines with every "N% of order" line re-priced off the rest
  // (`resolvePercentLines`). Derived on every change rather than recomputed
  // in each handler, for the same reason `totals` is — a quantity edit, a new
  // line, a removed one or a reloaded document must all move the ESP line,
  // and a handler that forgot would be a silent wrong number on a quote.
  const products = useMemo(() => resolvePercentLines(formData.products), [formData.products]);

  // The ONE source of truth for displayed money. Recomputes whenever
  // the line items OR any discount/tax input changes — which means it
  // is always correct after data-load, catalog auto-fill, line
  // add/remove, and discount edits (the paths the old per-handler
  // recompute missed).
  const totals = useMemo(
    () =>
      computeInvoiceTotals({
        products,
        marketDiscount: formData.market_discount,
        resellerDiscount: formData.reseller_discount,
        additionalDiscountAmount: formData.additional_discount_amount,
        additionalDiscountPercentage: formData.additional_discount_percentage,
        taxPercentage: formData.tax_percentage,
      }),
    [
      products,
      formData.market_discount,
      formData.reseller_discount,
      formData.additional_discount_amount,
      formData.additional_discount_percentage,
      formData.tax_percentage,
    ],
  );

  // Load the customer id → row pool. Since 2026-09-15 the picker searches the
  // server itself (customer-or-reseller-picker.tsx); this list is what the
  // `?customerId=` preselect, the Otto draft and the reopen loaders
  // (`resolveFormCustomer`) look a customer up in.
  const loadContacts = async () => {
    try {
      // Load the full customer list. It needs only id / name / company /
      // email / dealers — the selected customer's deal is fetched separately
      // (dealsApi.getDeals) — so request the LIGHT payload with
      // include_deals=false. Embedding every customer's deals +
      // invoices + ActiveStorage files across 500 rows N+1'd the render past
      // the 10s axios cap, which surfaced as the "Failed to load customers"
      // toast on BOTH invoice/quote create and edit. See crm-api customers#index.
      const response = await api.get('/customers?per_page=500&include_deals=false');
      setContacts(firstArrayField(response.data, ['customers', 'contacts']));
    } catch (error) {
      console.error('Error loading contacts:', error);
      toast({
        title: 'Error',
        description: 'Failed to load customers',
        variant: 'destructive'
      });
    } finally {
      // Mark the attempt complete either way — a failed/empty customers fetch
      // must not permanently block the document loaders (they snapshot-fallback).
      setContactsLoaded(true);
    }
  };

  // Load users for sales person dropdown
  const loadUsers = async () => {
    try {
      // Use the salespeople endpoint which is accessible to all authenticated users
      const response = await api.get('/users/salespeople');
      const users = firstArrayField(response.data, ['users']);

      setUsers(users);

      // Set current user as default sales person
      if (user && users.length > 0) {
        const currentUser = users.find((u: any) => u.id === user.id) || user;
        setSelectedSalesPerson(currentUser);
        setFormData(prev => ({
          ...prev,
          sales_person: personDisplayName(currentUser)
        }));
      }
    } catch (error) {
      console.error('Error loading users:', error);
      // If we can't load users but have current user, still set them as default
      if (user) {
        setSelectedSalesPerson(user);
        setFormData(prev => ({
          ...prev,
          sales_person: personDisplayName(user as any)
        }));
      }
    } finally {
      // The salespeople list is optional enrichment (name matching only) — a
      // failed/empty fetch must not block the document loaders.
      setUsersLoaded(true);
    }
  };

  // Load product hierarchy for cascading dropdowns
  const loadProductHierarchy = async () => {
    try {
      setIsLoadingProducts(true);
      
      const hierarchy = await productsApi.getProductHierarchy();
      setProductHierarchy(hierarchy);
      
    } catch (error) {
      console.error('❌ Error loading product hierarchy:', error);
      console.error('Error details:', error);
      setProductHierarchy({ products: [], productFeatures: {} });
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // Load customer deals and automatically select the first one
  const loadCustomerDeals = async (customerId: string): Promise<Deal | null> => {
    try {
      setIsLoadingDeals(true);
      
      const response = await dealsApi.getDeals({ customer_id: customerId });
      
      // Try different possible data structures
      let deals = response.customer_deals || (response as any).deals || (response as any).data?.customer_deals || (response as any).data?.deals || [];
      
      
      if (deals.length === 0) {
        // Create a default deal for this customer
        const defaultDeal = await dealsApi.createDeal({
          customer_id: customerId,
          title: 'Default Deal',
          description: 'Automatically created deal for invoice generation',
          amount: 0,
          deal_type: 'simple_purchase'
        });
        return defaultDeal;
      } else {
        const firstDeal = deals[0];
        return firstDeal;
      }
    } catch (error) {
      console.error('Error loading customer deals:', error);
      toast({
        title: 'Error',
        description: 'Failed to load customer deals',
        variant: 'destructive'
      });
      return null;
    } finally {
      setIsLoadingDeals(false);
    }
  };

  // Get invoice/quote count for customer to generate next document number
  const getCustomerDocumentCount = async (customerId: string, documentType: 'invoice' | 'quote'): Promise<number> => {
    try {
      
      // Use the existing invoices endpoint with customer_id filter
      const requestUrl = `/invoices?customer_id=${customerId}&per_page=100`;
      
      const response = await api.get(requestUrl);
      
      
      // Try different possible response structures and FILTER by customer_id and document_type
      let count = 0;
      let documentsForCustomer = [];
      
      // First, get the invoice data array
      let invoicesData = [];
      if (response.data?.data && Array.isArray(response.data.data)) {
        invoicesData = response.data.data;
      } else if (response.data?.invoices && Array.isArray(response.data.invoices)) {
        invoicesData = response.data.invoices;
      } else if (Array.isArray(response.data)) {
        invoicesData = response.data;
      }
      
      // Filter documents to only include this customer's documents of the specified type
      if (invoicesData.length > 0) {
        documentsForCustomer = invoicesData.filter((invoice: any) => {
          const matchesCustomer = invoice.customer_id && invoice.customer_id.toString() === customerId.toString();
          if (!matchesCustomer) return false;
          // Determine document type — explicit column, else the number pattern,
          // else default invoice (backward-compatible with pre-`document_type`
          // rows). See `generate-invoice-form/documentNumber.ts`.
          return inferDocumentType(invoice.document_type, invoice.invoice_number) === documentType;
        });

        // The next number counts up from the HIGHEST existing sequence (seeded
        // so an empty set yields 0 for invoices / 499 for quotes).
        count = highestSequence(
          documentsForCustomer.map((doc: any) => doc.invoice_number),
          documentType,
        );
      } else if (response.data?.pagination?.total_count !== undefined) {
        // If we got pagination info and no data (which means the filter worked server-side)
        // For now, we can't filter by document_type on server side, so this won't be accurate
        // We'll keep this for backward compatibility
        count = response.data.pagination.total_count;
      }
      
      return count;
    } catch (error) {
      console.error(`Error getting customer ${documentType} count:`, error);
      // Return appropriate fallback based on document type
      // For quotes: return 499 so next will be 500
      // For invoices: return 0 so next will be 001
      return documentType === 'quote' ? 499 : 0;
    }
  };

  // The highest existing sequence among documents XOP addressed to this
  // RESELLER and to no customer. Same shape as the customer counter above; the
  // customer segment of such a number is `0000`, which is the convention
  // crm-api's own quote mirror already writes.
  const getResellerDocumentCount = async (
    resellerId: string,
    documentType: 'invoice' | 'quote',
  ): Promise<number> => {
    try {
      const response = await api.get(`/invoices?bill_to_reseller_id=${resellerId}&per_page=100`);
      const rows: any[] = response.data?.data ?? response.data?.invoices ??
        (Array.isArray(response.data) ? response.data : []);
      const theirs = rows.filter(
        (row: any) =>
          !row.customer_id &&
          inferDocumentType(row.document_type, row.invoice_number) === documentType,
      );
      return highestSequence(theirs.map((row: any) => row.invoice_number), documentType);
    } catch (error) {
      console.error('Error counting reseller documents:', error);
      return sequenceSeed(documentType);
    }
  };

  const generateResellerDocumentNumber = async (
    reseller: any,
    documentType: 'invoice' | 'quote',
  ): Promise<string> => {
    const code = reseller?.code || '700';
    const maxSequence = await getResellerDocumentCount(String(reseller.id), documentType);
    // Customer segment `0000`: there is no customer, and inventing one would
    // make the number claim a relationship the document does not have.
    return formatDocumentNumber(code, 0, maxSequence + 1);
  };

  // Generate document number: [reseller_code]-[customer_id_4_digits]-[count_3_digits]
  // For invoices: sequence starts at 001, 002, 003...
  // For quotes: sequence starts at 500, then 501, 502, 503...
  const generateDocumentNumber = async (customer: any, documentType: 'invoice' | 'quote'): Promise<string> => {
    try {
      
      // Get reseller code (fallback to '700' if not available)
      const resellerCode = customer.reseller_code || customer.reseller?.code || '700';

      // getCustomerDocumentCount returns the HIGHEST existing sequence (0 for
      // invoices / 499 for quotes when none exist); +1 is the next to mint.
      const maxSequence = await getCustomerDocumentCount(customer.id.toString(), documentType);
      return formatDocumentNumber(resellerCode, customer.id, maxSequence + 1);
    } catch (error) {
      console.error(`Error generating ${documentType} number:`, error);
      return fallbackDocumentNumber(customer.id, documentType);
    }
  };

  const clearRecipientError = () => {
    setValidationErrors(prev => {
      if (!prev.customer) return prev;
      const newErrors = { ...prev };
      delete newErrors.customer;
      return newErrors;
    });
  };

  // Leave "addressed to a reseller" mode. Called on the way INTO the customer
  // flow, so a customer pick after a reseller pick (or after a `?resellerId=`
  // deep link) drops the reseller cleanly — the two are exclusive.
  const leaveResellerMode = () => {
    addressedResellerRef.current = null;
    setAddressedResellerId(null);
    setBillToReseller(null);
  };

  // Address the document to a RESELLER (2026-09-13): the ONE loader behind the
  // `?resellerId=` deep link, the picker's Resellers group (2026-09-15) and a
  // reopened reseller-addressed document. It loads the reseller, and then —
  // per the caller — fills the bill-to from their own legal identity
  // (`fillBillTo`: a NEW document; an edit keeps what was stored) and mints
  // `<code>-0000-NNN` (`mintNumber`: new / duplicate / convert; never an edit).
  // There is no customer and no agreement behind such a document, so the
  // customer state is cleared here rather than left for each caller.
  const addressDocumentToReseller = async (
    resellerId: string,
    documentType: 'invoice' | 'quote',
    opts: { fillBillTo: boolean; mintNumber: boolean },
  ): Promise<void> => {
    addressedResellerRef.current = resellerId;
    setAddressedResellerId(resellerId);
    setSelectedContact(null);
    setSelectedDeal(null);
    // The document is billed to THEM, so the stored recipient is the
    // reseller — the same `bill_to_reseller_id` the customer flow sets
    // from its Bill To dropdown.
    setBillToRecipientType('reseller');
    setSelectedDealerId(null);
    clearRecipientError();

    try {
      if (opts.mintNumber) setIsGeneratingInvoiceNumber(true);
      const reseller: any = await resellersApiService.getById(resellerId);
      // Superseded by a later pick while this was in flight — drop it.
      if (addressedResellerRef.current !== resellerId || !reseller) return;

      setBillToReseller(reseller);
      if (opts.fillBillTo) {
        setFormData(prev => ({
          ...prev,
          customer_id: null,
          customer_deal_id: null,
          ...addressFields('bill_to', {
            company: reseller.legal_name || reseller.name,
            address: reseller.address,
            city: reseller.city,
            state: reseller.state,
            zip: reseller.postal_code,
            country: reseller.country,
          }),
          // Nothing ships to a reseller's own invoice, and no customer means
          // no contact persons: clear what a previous customer pick filled.
          ...addressFields('ship_to', {}),
          bill_to_contact_id: null,
          bill_to_email: '',
          bill_to_contact_name: '',
          ship_to_contact_id: null,
          ship_to_email: '',
          ship_to_contact_name: '',
        }));
      }

      if (opts.mintNumber) {
        const number = await generateResellerDocumentNumber(reseller, documentType);
        if (addressedResellerRef.current === resellerId) {
          setFormData(prev => ({ ...prev, invoice_number: number }));
        }
      }
    } catch (error) {
      console.error('Error loading the reseller to bill:', error);
      toast({
        title: 'Warning',
        description: 'Failed to load the reseller or generate the document number',
        variant: 'destructive',
      });
    } finally {
      if (opts.mintNumber) setIsGeneratingInvoiceNumber(false);
    }
  };

  // The `?resellerId=` deep link (the reseller profile's "New invoice"): a NEW
  // document addressed to that reseller. Runs once per id, never on an edit.
  useEffect(() => {
    if (!billToResellerIdProp || isEditMode) return;
    void addressDocumentToReseller(String(billToResellerIdProp), preSelectedDocumentType, {
      fillBillTo: true,
      mintNumber: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billToResellerIdProp, isEditMode]);

  // Auto-fill customer address when customer is selected and automatically
  // select first deal. `contact` is the customer ROW (from the preloaded list
  // or straight from the picker's search hit); null clears the customer.
  const selectCustomer = async (contact: any | null, overrideDocumentType?: 'invoice' | 'quote') => {
    // A customer and a reseller are exclusive recipients.
    if (contact) leaveResellerMode();

    setSelectedContact(contact || null);

    // Clear customer validation error when customer is selected
    if (contact) clearRecipientError();

    // Clear previous deal selection when changing customer
    setSelectedDeal(null);
    
    // Reset Bill To recipient selection when customer changes
    if (!contact) {
      setBillToRecipientType(null);
      setSelectedDealerId(null);
    }
    
    if (contact) {
      try {
        // Set loading state for invoice number generation
        setIsGeneratingInvoiceNumber(true);
        
        // Load deals for this customer and automatically select the first one
        const firstDeal = await loadCustomerDeals(String(contact.id));
        
        if (firstDeal) {
          setSelectedDeal(firstDeal);
        }
        
        // Auto-fill addresses based on reseller logic
        const updateData: any = {
          customer_id: contact.id,
          customer_deal_id: firstDeal ? parseInt(firstDeal.id) : null,
        };
        
        // Only generate new document number when CREATING (not editing)
        if (!isEditMode) {
          // Generate document number automatically based on document type
          // Use overrideDocumentType if provided, otherwise fall back to formData.document_type
          const documentTypeToUse = overrideDocumentType || formData.document_type;
          const generatedDocumentNumber = await generateDocumentNumber(contact, documentTypeToUse);
          updateData.invoice_number = generatedDocumentNumber; // Auto-generated document number
        } else {
          // In edit mode, preserve the existing document number
        }

        // SHIP TO is always the customer's own address; BILL TO depends on the
        // recipient (reseller / dealer / customer). The reseller wall keeps the
        // reseller-name access in THIS gated branch (resellerAccess && …); the
        // pure `addressFields` mapper only shapes the fields.
        const customerSource = {
          company: contact.company_name,
          address: contact.address,
          city: contact.city,
          state: contact.state,
          zip: contact.zip,
          country: contact.country,
        };

        // Set default Bill To recipient based on reseller/dealers availability.
        // Reseller wall: skip the reseller + dealer recipients entirely for a
        // viewer without crm:resellers — they bill the customer directly (the
        // final branch), and no reseller name ever reaches the bill-to fields.
        if (resellerAccess && contact.reseller) {
          // Default to reseller (BILL TO = reseller, even if fields are empty).
          setBillToRecipientType('reseller');
          setSelectedDealerId(null);
          Object.assign(
            updateData,
            addressFields('bill_to', {
              company: contact.reseller.name,
              address: contact.reseller.address,
              city: contact.reseller.city,
              state: contact.reseller.state,
              zip: contact.reseller.postal_code,
              country: contact.reseller.country,
            }),
            addressFields('ship_to', customerSource),
          );
        } else if (resellerAccess && contact.dealers && contact.dealers.length > 0) {
          // Default to first dealer if no reseller.
          setBillToRecipientType('dealer');
          const firstDealer = contact.dealers[0];
          setSelectedDealerId(firstDealer.id.toString());
          Object.assign(
            updateData,
            addressFields('bill_to', {
              company: firstDealer.name,
              address: firstDealer.address,
              city: firstDealer.city,
              state: firstDealer.state,
              // Handle both postal_code and zip for compatibility.
              zip: firstDealer.postal_code || firstDealer.zip,
              country: firstDealer.country,
            }),
            addressFields('ship_to', customerSource),
          );
        } else {
          // No reseller or dealers — BILL TO and SHIP TO both = customer address.
          setBillToRecipientType(null);
          setSelectedDealerId(null);
          Object.assign(
            updateData,
            addressFields('bill_to', customerSource),
            addressFields('ship_to', customerSource),
          );
        }

        // Default the Bill To + Ship To recipient to the customer's first
        // contact person, if any — always scoped to THIS customer's own list.
        const firstContact = (contact.customer_contacts || [])[0] || null;
        updateData.bill_to_contact_id = firstContact ? firstContact.id : null;
        updateData.bill_to_email = firstContact?.email || '';
        updateData.bill_to_contact_name = firstContact?.full_name || firstContact?.display_name || '';
        updateData.ship_to_contact_id = firstContact ? firstContact.id : null;
        updateData.ship_to_email = firstContact?.email || '';
        updateData.ship_to_contact_name = firstContact?.full_name || firstContact?.display_name || '';

        setFormData(prev => ({
          ...prev,
          ...updateData
        }));

        
      } catch (error) {
        console.error('Error during customer selection:', error);
        toast({
          title: 'Warning',
          description: 'Failed to generate invoice number or load deals automatically',
          variant: 'destructive'
        });
      } finally {
        setIsGeneratingInvoiceNumber(false);
      }
    } else {
      // If no contact found, clear everything
      setSelectedDeal(null);
      setFormData(prev => ({
        ...prev,
        customer_id: null,
        customer_deal_id: null,
        invoice_number: '', // Clear invoice number too
        // Clear the recipient contact persons too (no customer → no contacts).
        bill_to_contact_id: null,
        bill_to_email: '',
        bill_to_contact_name: '',
        ship_to_contact_id: null,
        ship_to_email: '',
        ship_to_contact_name: '',
      }));
    }
  };

  // Select a customer BY ID from the preloaded list — the preselect deep link
  // (`?customerId=`) and the Otto draft resolve ids, not rows.
  const handleCustomerSelect = (customerId: string, overrideDocumentType?: 'invoice' | 'quote') =>
    selectCustomer(contacts.find(c => c.id.toString() === customerId) || null, overrideDocumentType);

  // The picker's pick: a customer runs the ordinary customer flow (a search
  // hit that is not in the preloaded 500 is adopted into it, so the preselect
  // and reopen paths keep finding it by id); a reseller puts the form into
  // exactly the state the `?resellerId=` deep link does; clearing empties
  // whichever of the two was set.
  const handleRecipientChange = (option: RecipientOption | null) => {
    if (!option) {
      if (billsAReseller) {
        leaveResellerMode();
        setBillToRecipientType(null);
        setFormData(prev => ({
          ...prev,
          invoice_number: '',
          ...addressFields('bill_to', {}),
        }));
      } else {
        void selectCustomer(null);
      }
      return;
    }
    if (option.kind === 'reseller') {
      // Show the name at once; the loader replaces it with the full record.
      setBillToReseller(option.reseller);
      void addressDocumentToReseller(option.id, formData.document_type, { fillBillTo: true, mintNumber: true });
      return;
    }
    const known = contacts.find(c => String(c.id) === option.id);
    if (!known) setContacts(prev => [...prev, option.customer]);
    void selectCustomer(known ?? option.customer);
  };

  // What the picker shows as selected: the reseller the document is addressed
  // to (its name once loaded — a deep link arrives with only the id), else
  // the selected customer, else nothing.
  const recipientValue: RecipientOption | null = billsAReseller
    ? resellerOption(billToReseller ?? { id: addressedResellerId, name: 'Loading…' })
    : selectedContact
      ? customerOption(selectedContact)
      : null;

  // Handle document type change and regenerate number
  const handleDocumentTypeChange = (value: string) => {
    const newDocumentType = value as 'invoice' | 'quote';

    setFormData(prev => ({
      ...prev,
      document_type: newDocumentType,
      terms: defaultTerms(newDocumentType)
    }));

    // Only regenerate document number when CREATING (not editing). A document
    // addressed to a reseller re-mints its `<code>-0000-NNN` from the reseller
    // sequence, exactly as a customer's re-mints from the customer's.
    const regenerate = billsAReseller && billToReseller
      ? () => generateResellerDocumentNumber(billToReseller, newDocumentType)
      : selectedContact
        ? () => generateDocumentNumber(selectedContact, newDocumentType)
        : null;
    if (!isEditMode && regenerate) {
      setIsGeneratingInvoiceNumber(true);
      regenerate()
        .then(newDocumentNumber => {
          setFormData(prev => ({ ...prev, invoice_number: newDocumentNumber }));
        })
        .catch(error => {
          console.error('Error regenerating document number:', error);
          toast({
            title: 'Warning',
            description: 'Failed to regenerate document number',
            variant: 'destructive'
          });
        })
        .finally(() => {
          setIsGeneratingInvoiceNumber(false);
        });
    } else if (isEditMode) {
    }
  };

  // Resolve one of the SELECTED customer's own contacts by id (never the global
  // contact directory — the picker is scoped to this one company).
  const contactById = (contactId: number | null) =>
    contactId == null
      ? null
      : (selectedContact?.customer_contacts || []).find((c: any) => c.id === contactId) || null;

  // Set the Bill To / Ship To recipient contact person and mirror its email +
  // name for display. The server re-snapshots email/name from the id on save.
  const handleContactChange = (section: 'bill_to' | 'ship_to', contactId: number | null) => {
    const c = contactById(contactId);
    setFormData(prev => ({
      ...prev,
      [`${section}_contact_id`]: contactId,
      [`${section}_email`]: c?.email || '',
      [`${section}_contact_name`]: c?.full_name || c?.display_name || '',
    }));
  };

  // Add product line
  const addProductLine = () => {
    setFormData(prev => ({
      ...prev,
      products: [...prev.products, createProductLine(Date.now().toString())]
    }));
  };

  // Remove product line
  const removeProductLine = (productId: string) => {
    setFormData(prev => ({
      ...prev,
      products: removeProduct(prev.products, productId)
    }));
    // Clean up price input state for removed product
    setPriceInputs(prev => {
      const updated = { ...prev };
      delete updated[productId];
      return updated;
    });
  };

  // NOTE: totals are NOT recomputed in these handlers anymore. They are
  // derived by the `totals` memo from products + discount/tax inputs, so
  // every mutation path (incl. data-load, catalog auto-fill, line
  // add/remove) stays consistent automatically. Handlers only update
  // their own field.

  // Update a product line. Keeps the per-line `amount` (= seats ×
  // chargePerSeat) in sync so the read-only Amount cell is correct; the
  // memo turns that into Sub Total / Total.
  const updateProduct = (productId: string, field: keyof InvoiceProduct, value: any) => {
    // Clear validation errors for this field when user makes changes
    if (hasAttemptedSubmit && (validationErrors.products?.[productId] as any)?.[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors.products && newErrors.products[productId]) {
          const prodErrors = { ...newErrors.products[productId] };
          delete (prodErrors as any)[field];
          if (Object.keys(prodErrors).length === 0) {
            delete newErrors.products[productId];
            if (Object.keys(newErrors.products).length === 0) {
              delete newErrors.products;
            }
          } else {
            newErrors.products[productId] = prodErrors;
          }
        }
        return newErrors;
      });
    }

    setFormData(prev => ({
      ...prev,
      products: updateProductField(prev.products, productId, field, value),
    }));
  };

  const updateTaxPercentage = (taxPercentage: number) => {
    setFormData(prev => ({ ...prev, tax_percentage: taxPercentage }));
  };

  const updateMarketDiscount = (marketDiscount: number) => {
    setFormData(prev => ({ ...prev, market_discount: marketDiscount }));
  };

  const updateResellerDiscount = (resellerDiscount: number) => {
    setFormData(prev => ({ ...prev, reseller_discount: resellerDiscount }));
  };

  const updateAdditionalDiscountAmount = (amount: number) => {
    setFormData(prev => ({ ...prev, additional_discount_amount: amount }));
  };

  const updateAdditionalDiscountPercentage = (percentage: number) => {
    setFormData(prev => ({ ...prev, additional_discount_percentage: percentage }));
  };

  // Handle cascading product selection
  const handleProductModeChange = (productRowId: string, mode: ProductSelectionMode) => {
    setFormData(prev => ({ ...prev, products: withProductMode(prev.products, productRowId, mode) }));
    // Dropping the catalogue pick must also drop its typed price echo, or the
    // Unit Price cell keeps showing the old number over a now-empty line.
    setPriceInputs(prev => {
      const next = { ...prev };
      delete next[productRowId];
      return next;
    });
  };

  /**
   * Cascading catalogue selection. TWO levels since 2026-09-13 — a product,
   * then one of its features — and the feature is where the price lives, so
   * picking one fills the part number, the description AND the unit price.
   */
  const handleCascadingSelection = (productRowId: string, level: 'product' | 'feature', selectedId: number) => {
    if (level === 'feature') {
      const product = formData.products.find((p) => p.id === productRowId);
      const feature = product?.selectedProductId
        ? productHierarchy.productFeatures[product.selectedProductId]?.find((f) => f.id === selectedId)
        : undefined;
      if (!feature) return;

      setFormData(prev => ({
        ...prev,
        products: applyCatalogFeature(prev.products, productRowId, feature),
      }));
      // `priceInputs` is the raw text the Unit Price field shows while it is
      // being typed in; it shadows the numeric value, so an auto-fill has to
      // write it too or the cell would still read whatever was typed before.
      // A feature with no unit price of its own (included, percent, unpriced)
      // leaves the echo EMPTY: an unpriced line then shows its placeholder
      // for the operator to fill, and a percent line's cell reads the
      // resolver, never this echo.
      const unitPrice = unitPriceFor(feature);
      setPriceInputs(prev => ({
        ...prev,
        [productRowId]: unitPrice > 0 ? unitPrice.toFixed(2) : '',
      }));
      return;
    }

    setFormData(prev => {
      const updatedProducts = prev.products.map(product => {
        if (product.id !== productRowId) return product;
        const updated = { ...product };
        updated.selectedProductId = selectedId;
        updated.selectedFeatureId = undefined; // Clear the dependent selection

        // If the product has no features, auto-fill from the product itself.
        const selectedProduct = productHierarchy.products.find(p => p.id === selectedId);
        if (selectedProduct && (productHierarchy.productFeatures[selectedId]?.length ?? 0) === 0) {
          updated.partNumber = selectedProduct.part_number;
          updated.description = selectedProduct.description;
        }
        return updated;
      });

      return { ...prev, products: updatedProducts };
    });
  };

  // Validate form and collect all errors
  const validateForm = () =>
    validateInvoiceForm({
      hasContact: !!selectedContact,
      invoiceNumber: formData.invoice_number,
      hasDeal: !!selectedDeal,
      products,
      totalAmount: totals.totalAmount,
      billsAReseller,
    });

  // Surface server-side validation errors the SAME way client-side ones
  // are shown: a persistent red banner (`general`) + the message anchored
  // to the due-date field. Previously these only went to a toast that
  // auto-dismissed (and the edit path showed just the first one).
  const applyServerErrors = (errors: string[] | undefined) => {
    const { general, dueDate } = splitInvoiceServerErrors(errors ?? []);
    setValidationErrors(prev => {
      const next = { ...prev, general };
      if (dueDate !== undefined) next.dueDate = dueDate;
      else delete next.dueDate;
      return next;
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle form submission
  const handleSubmit = async () => {
    setHasAttemptedSubmit(true);
    
    const errors = validateForm();
    setValidationErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      // Scroll to first error
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors below before submitting.',
        variant: 'destructive'
      });
      return;
    }

    // Which reseller the document is billed to, from the Bill To dropdown:
    // the customer's reseller, or one of its dealers (dealers are resellers).
    const billToResellerId: number | null = billsAReseller
      // Addressed to the reseller ITSELF — there is no customer to read one off.
      ? Number(addressedResellerId)
      : billToRecipientType === 'reseller'
        ? (selectedContact?.reseller?.id ?? null)
        : billToRecipientType === 'dealer' && selectedDealerId
          ? Number(selectedDealerId)
          : null;
    const invoiceData = buildInvoicePayload({
      contact: selectedContact,
      dealId: selectedDeal ? parseInt(selectedDeal.id) : null,
      // The RESOLVED lines: a percentage line goes out with the number the
      // operator saw. crm-api re-derives it anyway, so the two agree.
      formData: { ...formData, products },
      totals,
      billToResellerId,
      scheduling: {
        enableRecurring,
        recurringFrequency,
        recurringInterval,
        recurringDayOfWeek,
        recurringDayOfMonth,
        recurringStartDate,
        recurringGenerateDaysBeforePeriodEnd,
      },
    });

    // If in edit mode, call update API directly
    if (isEditMode && invoiceId) {
      try {
        // Extract scheduling data before updating invoice
        const schedulingData = invoiceData.scheduling;
        delete invoiceData.scheduling;

        const response = await invoicesApi.update(invoiceId, invoiceData);

        if (response.success) {
          // Clear any prior server validation banner/field error.
          setValidationErrors(prev => { const n = { ...prev }; delete n.general; delete n.dueDate; return n; });
          let schedulingMessage = '';

          // Handle scheduling if enabled (same logic as create mode)
          if (schedulingData && formData.document_type === 'invoice') {
            // Handle recurring schedule
            if (schedulingData.recurring) {
              try {
                // Import the API dynamically to avoid circular dependency
                const { invoiceSchedulesApi } = await import('@crm-src/lib/invoice-schedules-api');

                const scheduleData = {
                  customer_id: selectedContact?.id ?? null,
                  source_invoice_id: invoiceId,
                  name: `Recurring - ${formData.invoice_number}`,
                  frequency: schedulingData.recurring.frequency,
                  interval: schedulingData.recurring.interval,
                  day_of_week: schedulingData.recurring.day_of_week,
                  day_of_month: schedulingData.recurring.day_of_month,
                  start_date: schedulingData.recurring.start_date,
                  generate_days_before_period_end:
                    schedulingData.recurring.generate_days_before_period_end,
                  payment_terms_days: 30
                };

                // Update existing schedule or create new one
                const recurringResponse = existingScheduleId
                  ? await invoiceSchedulesApi.update(existingScheduleId, scheduleData)
                  : await invoiceSchedulesApi.create(scheduleData);

                if (recurringResponse.success) {
                  schedulingMessage = existingScheduleId
                    ? ' with recurring billing updated'
                    : ' with recurring billing set up';
                }
              } catch (error) {
                console.error('Failed to save recurring schedule:', error);
                toast({
                  title: 'Warning',
                  description: 'Invoice updated but recurring setup failed. You can set it up later.',
                  variant: 'default'
                });
              }
            }
          }

          toast({
            title: 'Success',
            description: 'Invoice updated successfully' + schedulingMessage
          });

          if (onInvoiceUpdated) {
            onInvoiceUpdated();
          }
        } else {
          // Every server validation message, in the persistent banner +
          // anchored to the due-date field (was: only response.errors[0]
          // in a toast that auto-dismissed).
          applyServerErrors(response.errors);
        }
      } catch (error: any) {
        console.error('Error updating invoice:', error);
        applyServerErrors(
          error?.response?.data?.errors ?? [
            error?.response?.data?.error || error?.message || 'Failed to update invoice',
          ],
        );
      }
      return;
    }

    // Create path: the parent owns the API call. It reports back so we
    // can surface server validation in this form's banner + field
    // instead of the parent silently toasting it. `void` stays
    // supported for callers that haven't migrated.
    const result = await Promise.resolve(onSubmit(invoiceData));
    if (result && result.success === false) {
      applyServerErrors(result.errors);
      return;
    }
    // Success (or a not-yet-migrated void caller): clear any stale
    // server errors so a later failure can't show a ghost banner.
    setValidationErrors(prev => { const n = { ...prev }; delete n.general; delete n.dueDate; return n; });
  };


  // Load contacts and users on mount
  useEffect(() => {
    loadContacts();
    loadUsers();
    loadProductHierarchy();
  }, [user]);

  // The trigger props (invoiceId / duplicateFrom / quoteToConvert) may be set a
  // render AFTER mount (the parent page reads them from the URL in its own
  // effect). Flip into the loading state as soon as one appears so the reader
  // never sees the "Customer or reseller" picker flash before the document loads.
  useEffect(() => {
    if (invoiceId || duplicateFrom || quoteToConvert || ottoDraft) {
      setIsLoadingInvoice(true);
    }
  }, [invoiceId, duplicateFrom, quoteToConvert, ottoDraft]);

  // Load invoice data when in edit mode. Gate on the list fetches having
  // COMPLETED (not on them being non-empty) so an empty/failed customers or
  // salespeople list can't leave Edit stuck on a blank form.
  useEffect(() => {
    if (isEditMode && invoiceId && contactsLoaded && usersLoaded) {
      loadInvoiceData();
    }
  }, [isEditMode, invoiceId, contactsLoaded, usersLoaded]);

  // Function to load invoice data for editing
  // Restore the Bill To recipient from a stored `bill_to_reseller_id` when a
  // document is loaded (edit / convert): the customer's reseller → 'reseller',
  // anything else → that dealer. With NO customer the document is addressed to
  // the reseller itself, and the recipient is that reseller.
  const setBillToRecipient = (resellerId: number | null, customer: any) => {
    if (!resellerId) { setBillToRecipientType(null); setSelectedDealerId(null); return; }
    if (!customer || customer?.reseller?.id === resellerId) { setBillToRecipientType('reseller'); setSelectedDealerId(null); return; }
    setBillToRecipientType('dealer');
    setSelectedDealerId(String(resellerId));
  };

  const loadInvoiceData = () => loadInvoiceDataUtil({
    invoiceId,
    contacts,
    users,
    setIsLoadingInvoice,
    setSelectedContact,
    setBillToRecipient,
    addressToReseller: addressDocumentToReseller,
    setSelectedSalesPerson,
    setSelectedDeal,
    setFormData,
    setPriceInputs,
    setExistingScheduleId,
    setEnableRecurring,
    setRecurringFrequency,
    setRecurringInterval,
    setRecurringStartDate,
    setRecurringDayOfWeek,
    setRecurringDayOfMonth,
  });

  // Load quote data when converting to invoice (gate on lists LOADED, not non-empty).
  useEffect(() => {
    if (quoteToConvert && contactsLoaded && usersLoaded && !isEditMode) {
      loadQuoteDataForConversion();
    }
  }, [quoteToConvert, contactsLoaded, usersLoaded, isEditMode]);

  // Load invoice data when duplicating (gate on lists LOADED, not non-empty).
  useEffect(() => {
    if (duplicateFrom && contactsLoaded && usersLoaded && !isEditMode) {
      loadInvoiceDataForDuplication();
    }
  }, [duplicateFrom, contactsLoaded, usersLoaded, isEditMode]);

  // Function to load quote data for conversion to invoice
  const loadQuoteDataForConversion = () => loadQuoteDataForConversionUtil({
    quoteToConvert,
    contacts,
    users,
    setIsLoadingInvoice,
    setSelectedContact,
    setBillToRecipient,
    addressToReseller: addressDocumentToReseller,
    setSelectedSalesPerson,
    setSelectedDeal,
    setFormData,
    setPriceInputs,
    setIsGeneratingInvoiceNumber,
    generateDocumentNumber,
  });

  // Function to load invoice data for duplication
  const loadInvoiceDataForDuplication = () => loadInvoiceDataForDuplicationUtil({
    duplicateFrom,
    contacts,
    users,
    setIsLoadingInvoice,
    setSelectedContact,
    addressToReseller: addressDocumentToReseller,
    setSelectedSalesPerson,
    setSelectedDeal,
    setFormData,
    setPriceInputs,
    setIsGeneratingInvoiceNumber,
    generateDocumentNumber,
  });

  // Hydrate from an Otto Agent draft (gate on lists LOADED, not non-empty — same
  // as the other loaders). No source doc is fetched; the draft is authoritative.
  useEffect(() => {
    if (ottoDraft && contactsLoaded && usersLoaded && !isEditMode) {
      loadInvoiceDataFromDraftUtil({
        draft: ottoDraft,
        documentType: ottoDraft.document_type || preSelectedDocumentType,
        contacts,
        users,
        setIsLoadingInvoice,
        setSelectedContact,
        setSelectedSalesPerson,
        setSelectedDeal,
        setFormData,
        setPriceInputs,
        setIsGeneratingInvoiceNumber,
        generateDocumentNumber,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ottoDraft, contactsLoaded, usersLoaded, isEditMode]);

  // Handle preSelectedDocumentType changes
  useEffect(() => {
    if (preSelectedDocumentType !== formData.document_type) {
      setFormData(prev => ({ 
        ...prev, 
        document_type: preSelectedDocumentType,
        terms: defaultTerms(preSelectedDocumentType)
      }));
    }
  }, [preSelectedDocumentType]);

  // Auto-select customer if preSelectedCustomerId is provided
  useEffect(() => {

    if (preSelectedCustomerId && contacts.length > 0) {
      // Check if the preselected customer is already selected
      if (selectedContact && selectedContact.id.toString() === preSelectedCustomerId) {
        return;
      }

      
      // First try to find by database id
      let contact = contacts.find(c => c.id.toString() === preSelectedCustomerId);
      let searchMethod = 'database id';
      
      // If not found, try to find by customer_id (business identifier)
      if (!contact) {
        contact = contacts.find(c => {
          const customerIdStr = c.customer_id || '';
          // Check if customer_id contains the search term (e.g., "700-4042" contains "4042")
          return customerIdStr.includes(preSelectedCustomerId);
        });
        searchMethod = 'customer_id containing';
      }
      
      // If still not found, try exact customer_id match
      if (!contact) {
        contact = contacts.find(c => {
          const customerIdStr = c.customer_id || '';
          return customerIdStr === preSelectedCustomerId;
        });
        searchMethod = 'exact customer_id';
      }
      
      if (contact) {
        // Pass preSelectedDocumentType to ensure correct document type is used for number generation
        handleCustomerSelect(contact.id.toString(), preSelectedDocumentType); // Always use database id for selection
      } else {
        
        // Try to search for the customer if not found in initial load
        const searchForCustomer = async () => {
          try {
            const searchResponse = await api.get(`/customers?search=${preSelectedCustomerId}&per_page=100`);
            const searchResults = searchResponse.data?.data || searchResponse.data?.customers || searchResponse.data?.contacts || searchResponse.data || [];
            

            if (searchResults.length > 0) {
              // Add the search results to the contacts list if not already present
              const updatedContacts = [...contacts];
              searchResults.forEach((searchResult: any) => {
                if (!contacts.find((c: any) => c.id === searchResult.id)) {
                  updatedContacts.push(searchResult);
                }
              });
              setContacts(updatedContacts);

              // Try to auto-select from search results
              const foundCustomer = searchResults.find((c: any) =>
                c.id.toString() === preSelectedCustomerId ||
                (c.customer_id && c.customer_id.includes(preSelectedCustomerId))
              );
              
              if (foundCustomer) {
                // Pass preSelectedDocumentType to ensure correct document type is used for number generation
                handleCustomerSelect(foundCustomer.id.toString(), preSelectedDocumentType);
              }
            } else {
            }
          } catch (searchError) {
            console.error('Error searching for customer:', searchError);
          }
        };
        
        searchForCustomer();
      }
    }
  }, [preSelectedCustomerId, contacts]);

  if (isLoadingInvoice) {
    return (
      <Box
        data-testid="invoice-form-loading"
        sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, py: 8 }}
      >
        <CircularProgress />
        <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
          Loading {formData.document_type === 'quote' ? 'quote' : 'invoice'}…
        </Typography>
      </Box>
    );
  }

  // The recipient card. For an INVOICE it renders Bill To + Ship To (with
  // addresses) inside the columns row; for a QUOTE it renders just the customer's
  // Contact Person, which the parent places right under the customer selector
  // below. Defined once, placed by document type.
  const recipientCard = (
    <BillToShipToCard
      documentType={formData.document_type}
      selectedContact={selectedContact}
      billToFields={{
        company: formData.bill_to_company,
        address: formData.bill_to_address,
        city: formData.bill_to_city,
        state: formData.bill_to_state,
        zip: formData.bill_to_zip,
        country: formData.bill_to_country,
      }}
      shipToFields={{
        company: formData.ship_to_company,
        address: formData.ship_to_address,
        city: formData.ship_to_city,
        state: formData.ship_to_state,
        zip: formData.ship_to_zip,
        country: formData.ship_to_country,
      }}
      onBillToChange={(field, value) => setFormData(prev => ({ ...prev, [`bill_to_${field}`]: value }))}
      onShipToChange={(field, value) => setFormData(prev => ({ ...prev, [`ship_to_${field}`]: value }))}
      onBillToBulkChange={(updates) => setFormData(prev => ({ ...prev, ...updates }))}
      onShipToBulkChange={(updates) => setFormData(prev => ({ ...prev, ...updates }))}
      billToRecipientType={billToRecipientType}
      setBillToRecipientType={setBillToRecipientType}
      selectedDealerId={selectedDealerId}
      setSelectedDealerId={setSelectedDealerId}
      billToContactId={formData.bill_to_contact_id}
      shipToContactId={formData.ship_to_contact_id}
      onBillToContactChange={(id) => handleContactChange('bill_to', id)}
      onShipToContactChange={(id) => handleContactChange('ship_to', id)}
    />
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* General Error Banner — persistent (NOT a toast). Carries every
            server validation message via validationErrors.general. */}
        {validationErrors.general && (
          <Box sx={{ bgcolor: (t) => alpha(t.palette.error.main, 0.08), border: 1, borderColor: (t) => alpha(t.palette.error.main, 0.4), borderRadius: 1, p: 2, display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <Box sx={{ color: "error.main", flexShrink: 0, mt: "2px", display: "flex" }}><AlertTriangle size={20} /></Box>
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 500, color: "error.dark" }}>Validation Error</Typography>
              <Typography sx={{ fontSize: 14, color: "error.main", mt: 0.5, whiteSpace: "pre-line" }}>{validationErrors.general}</Typography>
            </Box>
          </Box>
        )}

        {/* WHO the document is for — one picker, customers and resellers
            (2026-09-15). Always visible; on an edit it shows the stored
            recipient. */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <CustomerOrResellerPicker
            value={recipientValue}
            onChange={handleRecipientChange}
            {...(validationErrors.customer ? { error: validationErrors.customer } : {})}
          />

          {/* Addressed to a RESELLER: say so. XOP's business with a reseller
              has no customer and no agreement behind it. */}
          {billsAReseller && (
            <Box
              data-cy="bill-to-reseller-banner"
              sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}
            >
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                Billing reseller
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                {billToReseller?.legal_name || billToReseller?.name || 'Loading…'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.disabled", mt: 0.5 }}>
                XOP Networks' own business with this reseller — no customer and no agreement behind it.
                It appears on their profile and in their portal to pay.
              </Typography>
            </Box>
          )}

          {/* Loading indicator while the picked customer's agreement is fetched */}
          {selectedContact && isLoadingDeals && (
            <Box sx={{ p: 1.5, fontSize: 14, color: "primary.main", bgcolor: (t) => t.palette.action.hover, borderRadius: 1, border: 1, borderColor: "divider" }}>
              Loading customer information for {selectedContact.company_name || selectedContact.full_name || selectedContact.name}...
            </Box>
          )}
        </Box>

        {/* Full Invoice Form - Show when customer is selected and deals are loaded */}
        {((selectedContact && !isLoadingDeals) || billsAReseller) && (
          <>
            {/* QUOTE: the recipient IS the customer's contact person, so it sits
                right under the customer selector — not in a separate "To" block.
                Invoices keep Bill To / Ship To in the columns row below. */}
            {formData.document_type !== 'invoice' && recipientCard}

            <Divider />

        {/* Invoice Details, Bill To, and Ship To in one row */}
        <Box sx={{ display: "grid", gap: 4, gridTemplateColumns: formData.document_type === 'invoice' ? "repeat(3, 1fr)" : "1fr" }}>
          {/* Invoice Details */}
          <InvoiceDetailsCard
            formData={{
              document_type: formData.document_type,
              invoice_number: formData.invoice_number,
              subject: formData.subject,
              issue_date: formData.issue_date,
              due_date: formData.due_date,
              service_from: formData.service_from,
              service_to: formData.service_to,
              po_number: formData.po_number,
              sales_person: formData.sales_person,
              terms: formData.terms,
            }}
            {...(validationErrors.dueDate ? { dueDateError: validationErrors.dueDate } : {})}
            onChange={(field, value) => {
              // Changing the issue date re-derives the due date to issue + 30
              // days (Net-30) — applyInvoiceFieldChange owns that invariant;
              // every other field is a plain assignment.
              setFormData(prev => applyInvoiceFieldChange(prev, field, value));
              // Editing either date invalidates a server due-date verdict —
              // clear the banner + field error so it isn't stale.
              if (field === 'due_date' || field === 'issue_date') {
                setValidationErrors(prev => { const n = { ...prev }; delete n.general; delete n.dueDate; return n; });
              }
            }}
            onDocumentTypeChange={handleDocumentTypeChange}
            isConvertingFromQuote={isConvertingFromQuote}
            isGeneratingInvoiceNumber={isGeneratingInvoiceNumber}
            users={users}
            selectedSalesPerson={selectedSalesPerson}
            canEditSalesPerson={canEditSalesPerson}
            lockedSalesPersonName={user ? personDisplayName(user as any) : ''}
            onSalesPersonChange={(value) => {
              const salesPerson = users.find(u => u.id.toString() === value);
              setSelectedSalesPerson(salesPerson || null);
              setFormData(prev => ({
                ...prev,
                sales_person: salesPerson ? (salesPerson.full_name || salesPerson.name || `${salesPerson.first_name || ''} ${salesPerson.last_name || ''}`.trim()) : ''
              }));
            }}
          />

          {/* Invoices show Bill To / Ship To here; a quote's recipient moved up
              under the customer selector (see above). */}
          {formData.document_type === 'invoice' && recipientCard}
        </Box>

        <Divider />

        {/* Products/Services */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <ProductsTable
            products={products}
            productHierarchy={productHierarchy}
            priceInputs={priceInputs}
            setPriceInputs={setPriceInputs}
            validationErrors={validationErrors.products}
            onAddProduct={addProductLine}
            onRemoveProduct={removeProductLine}
            onUpdateProduct={updateProduct}
            onProductModeChange={handleProductModeChange}
            onCascadingSelection={handleCascadingSelection}
          />

          <TotalsCard
            documentType={formData.document_type}
            subTotal={totals.subTotal}
            marketDiscount={formData.market_discount}
            resellerDiscount={formData.reseller_discount}
            additionalDiscountAmount={formData.additional_discount_amount}
            additionalDiscountPercentage={formData.additional_discount_percentage}
            taxPercentage={formData.tax_percentage}
            taxAmount={totals.taxAmount}
            totalAmount={totals.totalAmount}
            balanceDue={totals.balanceDue}
            marketDiscountAmount={totals.marketDiscountAmount}
            resellerDiscountAmount={totals.resellerDiscountAmount}
            additionalDiscountValue={totals.additionalDiscountValue}
            updateMarketDiscount={updateMarketDiscount}
            updateResellerDiscount={updateResellerDiscount}
            updateAdditionalDiscountAmount={updateAdditionalDiscountAmount}
            updateAdditionalDiscountPercentage={updateAdditionalDiscountPercentage}
            updateTaxPercentage={updateTaxPercentage}
          />
        </Box>
          </>
        )}
      </Box>

      {/* Scheduling Options - Only for invoices, not quotes */}
      <SchedulingCard
        documentType={formData.document_type}
        selectedContact={selectedContact}
        enableRecurring={enableRecurring}
        setEnableRecurring={setEnableRecurring}
        recurringFrequency={recurringFrequency}
        setRecurringFrequency={setRecurringFrequency}
        recurringInterval={recurringInterval}
        setRecurringInterval={setRecurringInterval}
        recurringStartDate={recurringStartDate}
        setRecurringStartDate={setRecurringStartDate}
        recurringDayOfWeek={recurringDayOfWeek}
        setRecurringDayOfWeek={setRecurringDayOfWeek}
        recurringDayOfMonth={recurringDayOfMonth}
        recurringGenerateDaysBeforePeriodEnd={recurringGenerateDaysBeforePeriodEnd}
        setRecurringGenerateDaysBeforePeriodEnd={setRecurringGenerateDaysBeforePeriodEnd}
        setRecurringDayOfMonth={setRecurringDayOfMonth}
      />

      {/* Form Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 2, borderTop: 1, borderColor: "divider", mt: 3 }}>
        <Button variant="outlined" color="inherit" onClick={onBack}>
          Back
        </Button>
        <Button variant="outlined" color="inherit" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isCreating || (!billsAReseller && (!selectedContact || !selectedDeal || isLoadingDeals)) || isLoadingInvoice}>
          {isCreating
            ? (isEditMode ? 'Updating...' : 'Generating...')
            : isEditMode
              ? `Update ${formData.document_type === 'quote' ? 'Quote' : 'Invoice'}`
              : `Generate ${formData.document_type === 'quote' ? 'Quote' : 'Invoice'}`
          }
        </Button>
      </Box>
    </Box>
  );
};
